import type { QueryClient } from "@tanstack/react-query";
import { createAuthenticatedApi } from "@/lib/utils/api/api-client";
import { resolveEffectiveTenantNumber } from "@/lib/utils/env/effective-tenant";
import {
  type GetAppointmentBookingResponse,
  type GetBulkAppointmentBookingsItem,
  PrivatesAppointmentsApi,
} from "@/openapi/generated";
import {
  APPOINTMENT_BOOKINGS_BULK_MAX_IDS,
  appointmentBookingsQueryKey,
  uniqueSortedAppointmentIds,
} from "./appointment-bookings-query";

function chunkIds(ids: number[], size: number): number[][] {
  const chunks: number[][] = [];
  for (let i = 0; i < ids.length; i += size) {
    chunks.push(ids.slice(i, i + size));
  }
  return chunks;
}

/** One or more bulk calls → map of appointmentId → bookings (empty array if omitted). */
export async function fetchAppointmentBookingsByIds(
  accessToken: string,
  appointmentIds: number[],
): Promise<Map<number, GetAppointmentBookingResponse[]>> {
  const uniqueIds = uniqueSortedAppointmentIds(appointmentIds);
  const byId = new Map<number, GetAppointmentBookingResponse[]>();
  for (const id of uniqueIds) {
    byId.set(id, []);
  }

  if (uniqueIds.length === 0) return byId;

  const service = createAuthenticatedApi(PrivatesAppointmentsApi, accessToken);
  const tenantId = resolveEffectiveTenantNumber();

  for (const chunk of chunkIds(uniqueIds, APPOINTMENT_BOOKINGS_BULK_MAX_IDS)) {
    const response = await service.apiAppointmentsBookingsBulkPost({
      getBulkAppointmentBookingsRequest: { appointmentIds: chunk },
      xTenantID: tenantId,
    });

    for (const item of response.items ?? []) {
      const appointmentId = Number(item.appointmentId);
      if (!Number.isFinite(appointmentId) || appointmentId <= 0) continue;
      byId.set(
        appointmentId,
        Array.isArray(item.bookings) ? item.bookings : [],
      );
    }
  }

  return byId;
}

export function seedAppointmentBookingsQueries(
  queryClient: QueryClient,
  bookingsByAppointmentId: Map<number, GetAppointmentBookingResponse[]>,
) {
  for (const [appointmentId, bookings] of bookingsByAppointmentId) {
    queryClient.setQueryData(
      appointmentBookingsQueryKey(appointmentId),
      bookings,
    );
  }
}

export async function fetchAndSeedAppointmentBookingsBulk(
  queryClient: QueryClient,
  accessToken: string,
  appointmentIds: number[],
): Promise<GetBulkAppointmentBookingsItem[]> {
  const byId = await fetchAppointmentBookingsByIds(accessToken, appointmentIds);
  seedAppointmentBookingsQueries(queryClient, byId);
  return [...byId.entries()].map(([appointmentId, bookings]) => ({
    appointmentId,
    bookings,
  }));
}
